/**
 * Admin web token layer (DESIGN-V3, ADR-0017): the Lions Admin v3 design on top of the shared
 * tokens. Only the admin web loads it (`@lions/ui-tokens/admin.css` after `tokens.css`, and the
 * `admin*` exports in its MUI theme); the shared `palette`, `colors` and `semantic*` exports and
 * `tokens.css` stay as they are, because mobile reads them.
 *
 * Brand values are the brand book's digital primaries: navy #172557 for text and fills, deep navy
 * #13294B for the sidebar, gold #F9BD1D. The dark scheme is the design's navy-tinted ramp. Where a
 * design value fails WCAG it is darkened by the smallest step that passes (accessibility override,
 * logged in docs/prompts/progress.md, DESIGN-V3 Decisions).
 */
import { fontFamilies, fontWeights, palette, typography } from './foundation.js';
import { COLOR_SCHEME_SELECTOR, scaleVariables, webFontStack } from './css.js';
import { layout } from './scales.js';
import { semanticDark, semanticLight } from './semantic.js';
/** Brand book digital primaries; pinned by a guard test. */
export const adminBrand = {
    navy: '#172557',
    sidebarNavy: '#13294B',
    gold: '#F9BD1D',
    goldHover: '#FFCA45',
    goldDeep: '#E5A50F',
};
/** The design's navy-tinted greys (light) and navy ramp (dark), as used by the admin roles. */
export const adminPalette = {
    light: {
        page: '#F4F5F8',
        sunken: '#F7F8FA',
        tile: '#F1F3F7',
        track: '#E8EBF1',
        line: '#EEF0F4',
        border: '#E6E8EF',
        /** Table cells and secondary copy (design `--m3`). */
        ink3: '#3D4766',
        /** Muted copy (design `--m2`). */
        ink2: '#5B6584',
        /** Descriptions (design `--m1` #6B7491, darkened from 4.25:1 to 4.5:1 on the page). */
        ink1: '#67708D',
        goldText: '#875A00',
    },
    dark: {
        page: '#070C22',
        card: '#0F1838',
        sunken: '#0B1330',
        tile: '#16214A',
        track: '#0A1230',
        line: '#1A2550',
        border: '#1C2752',
        /** Selected segment and the navy hover in the dark scheme. */
        segment: '#24346E',
        navy: '#1C2C66',
        ink: '#F2F4FA',
        ink3: '#C6CEE6',
        ink2: '#A3ADCB',
        ink1: '#8E99BE',
        /** Placeholder and disabled copy; also the input border (the design's #2A3870 is 1.6:1). */
        ink5: '#6F7BA3',
        /** Earlier columns of a chart (the design's input border, #2A3870). */
        chartMuted: '#2A3870',
        goldText: '#F7C64A',
    },
    sidebar: {
        border: '#141E44',
        /** Inactive item text. */
        text: '#C4CBE3',
        /**
         * Group labels and footer copy: the design's #6F7BA3 is 3.5:1 on the sidebar and its #8C97BC
         * 4.3:1 on the app status card, so it is lightened to pass on both.
         */
        textSubtle: '#909BC0',
    },
    /** Fixed dark panels: match hero, login photo panel, phone mockups (both schemes). */
    fixed: {
        hero: '#050A1C',
        heroTextMuted: '#C9D1EA',
        phoneBezel: '#0D0F16',
        phoneScreen: '#08102B',
        /** Secondary text on gold (the design's #5A4508, darkened to 4.5:1 on the gradient end). */
        onGoldMuted: '#564104',
    },
    /** Chart series colours (both schemes; `muted` is the light scheme's earlier columns). */
    chart: {
        1: '#F9BD1D',
        2: '#4A7BFF',
        3: '#E5484D',
        4: '#8B5CF6',
        5: '#2E9D57',
        6: '#4A5DA8',
        muted: '#C9CFE0',
    },
};
const { light: L, dark: D, sidebar: SB, chart, fixed: F } = adminPalette;
/** Roles that exist only in the admin layer (both schemes). */
const sidebarRoles = {
    'sidebar-bg': adminBrand.sidebarNavy,
    'sidebar-border': SB.border,
    'sidebar-text': palette.neutral[0],
    'sidebar-text-muted': SB.text,
    'sidebar-text-subtle': SB.textSubtle,
    'sidebar-hover': 'rgba(255, 255, 255, 0.07)',
    'sidebar-panel': 'rgba(255, 255, 255, 0.05)',
    'sidebar-active': adminBrand.gold,
    'sidebar-active-text': adminBrand.navy,
    'sidebar-focus-ring': adminBrand.gold,
};
const chartRoles = {
    'chart-1': chart[1],
    'chart-2': chart[2],
    'chart-3': chart[3],
    'chart-4': chart[4],
    'chart-5': chart[5],
    'chart-6': chart[6],
};
/** Fixed in both schemes: dark panels, gold KPI cards and the switch. */
const fixedRoles = {
    'hero-bg': F.hero,
    'hero-text-muted': F.heroTextMuted,
    'phone-bezel': F.phoneBezel,
    'phone-screen': F.phoneScreen,
    'text-on-primary-muted': F.onGoldMuted,
    'on-primary-tile': 'rgba(23, 37, 87, 0.12)',
    // The design's white thumb on gold fails 3:1, so a checked thumb is navy (accessibility override).
    'switch-thumb': palette.neutral[0],
    'switch-thumb-checked': adminBrand.navy,
};
/** The navy top bar (until the v3 header replaces it) uses the sidebar navy. */
const topbar = {
    'topbar-bg': adminBrand.sidebarNavy,
    'topbar-text': palette.neutral[0],
    'topbar-text-muted': palette.navy[100],
    'topbar-hover': D.navy,
    'topbar-focus-ring': adminBrand.gold,
};
export const adminSemanticLight = {
    ...semanticLight,
    bg: L.page,
    surface: palette.neutral[0],
    'surface-raised': palette.neutral[0],
    'surface-sunken': L.sunken,
    text: adminBrand.navy,
    'text-secondary': L.ink3,
    'text-muted': L.ink2,
    'text-subtle': L.ink1,
    'text-on-primary': adminBrand.navy,
    border: L.border,
    'border-subtle': L.line,
    primary: adminBrand.gold,
    'primary-hover': adminBrand.goldHover,
    'primary-active': adminBrand.goldDeep,
    'primary-gradient-end': adminBrand.goldDeep,
    'primary-text': L.goldText,
    secondary: adminBrand.navy,
    'secondary-hover': D.navy,
    'secondary-text': adminBrand.navy,
    link: adminBrand.navy,
    'focus-ring': adminBrand.navy,
    'control-checked': adminBrand.navy,
    hover: 'rgba(23, 37, 87, 0.06)',
    selected: 'rgba(249, 189, 29, 0.16)',
    'table-header': L.sunken,
    'table-header-text': L.ink2,
    track: L.track,
    tile: L.tile,
    'segment-selected': palette.neutral[0],
    'header-bg': 'rgba(244, 245, 248, 0.88)',
    // The latest bar of a column chart: navy as in the design's legend (its gold bar is 1.7:1).
    'chart-latest': adminBrand.navy,
    'chart-muted': chart.muted,
    // The design's #C3C8D6 off track is 1.6:1 on white; the input border colour passes 3:1.
    'switch-track': semanticLight['border-strong'],
    ...topbar,
    ...fixedRoles,
    ...sidebarRoles,
    ...chartRoles,
};
export const adminSemanticDark = {
    ...semanticDark,
    bg: D.page,
    surface: D.card,
    'surface-raised': D.card,
    'surface-sunken': D.sunken,
    text: D.ink,
    'text-secondary': D.ink3,
    'text-muted': D.ink2,
    'text-subtle': D.ink1,
    'text-disabled': D.ink5,
    'text-on-primary': adminBrand.navy,
    border: D.border,
    'border-subtle': D.line,
    'border-strong': D.ink5,
    primary: adminBrand.gold,
    'primary-hover': adminBrand.goldHover,
    'primary-active': adminBrand.goldDeep,
    'primary-gradient-end': adminBrand.goldDeep,
    'primary-text': D.goldText,
    secondary: D.navy,
    'secondary-hover': D.segment,
    'table-header': D.sunken,
    'table-header-text': D.ink2,
    track: D.track,
    tile: D.tile,
    'segment-selected': D.segment,
    'header-bg': 'rgba(7, 12, 34, 0.82)',
    // The design's dark navy bar (#1C2C66) is 1.3:1 on the dark card; the chart blue stands out.
    'chart-latest': chart[2],
    // The earlier bars recede into the navy card as in the design (the light grey glared).
    'chart-muted': D.chartMuted,
    'switch-track': D.ink5,
    ...topbar,
    ...fixedRoles,
    ...sidebarRoles,
    ...chartRoles,
};
export const adminColorSchemes = { light: adminSemanticLight, dark: adminSemanticDark };
/** Body and headings are Montserrat (brand book: Black headings, Bold subheads, Medium body). */
export const adminFontFamilies = { ...fontFamilies, body: fontFamilies.heading };
export const adminFontWeights = { ...fontWeights, extrabold: 800, black: 900 };
const heading = adminFontFamilies.heading;
const body = adminFontFamilies.body;
/** The shared type scale in Montserrat with the design's weights, plus the v3 styles. */
export const adminTypography = {
    display: { ...typography.display, fontFamily: heading, fontWeight: 900 },
    h1: { ...typography.h1, fontFamily: heading, fontWeight: 800 },
    h2: { ...typography.h2, fontFamily: heading, fontWeight: 800 },
    h3: { ...typography.h3, fontFamily: heading, fontWeight: 800 },
    h4: { ...typography.h4, fontFamily: heading, fontWeight: 800 },
    bodyLarge: { ...typography.bodyLarge, fontFamily: body, fontWeight: 500 },
    body: { ...typography.body, fontFamily: body, fontWeight: 500 },
    label: { ...typography.label, fontFamily: body, fontWeight: 700 },
    caption: { ...typography.caption, fontFamily: body, fontWeight: 500 },
    overline: { ...typography.overline, fontFamily: body, fontWeight: 700 },
    /** Page titles: uppercase. */
    pageTitle: { fontFamily: heading, fontSize: 30, lineHeight: 32, fontWeight: 900 },
    cardTitle: { fontFamily: heading, fontSize: 16, lineHeight: 20, fontWeight: 800 },
    /** KPI numbers: tabular figures. */
    statLg: { fontFamily: heading, fontSize: 32, lineHeight: 36, fontWeight: 800 },
    statMd: { fontFamily: heading, fontSize: 24, lineHeight: 28, fontWeight: 800 },
    statSm: { fontFamily: heading, fontSize: 20, lineHeight: 24, fontWeight: 800 },
    bodySm: { fontFamily: body, fontSize: 13, lineHeight: 18, fontWeight: 500 },
    fieldLabel: { fontFamily: body, fontSize: 12, lineHeight: 16, fontWeight: 700 },
    /** Phone-preview copy only. */
    micro: { fontFamily: body, fontSize: 10, lineHeight: 12, fontWeight: 600 },
};
export const adminLayout = {
    ...layout,
    sidebarWidth: 248,
    /** The v3 header (the design's 68 px, snapped to 64). */
    topbarHeight: 64,
    panel: { ...layout.panel, xl: 1040, phone: 300 },
};
/** `adminCssVar('sidebar-bg')` → `'var(--sidebar-bg)'`, typed against the admin layer. */
export function adminCssVar(name) {
    return `var(--${name})`;
}
const px = (n) => `${n}px`;
/** Scale properties the admin layer adds or changes (everything else comes from tokens.css). */
export function adminScaleVariables() {
    const shared = scaleVariables();
    const styles = Object.entries(adminTypography).filter(([name]) => !(name in typography));
    const changed = {
        'font-body': webFontStack(adminFontFamilies.body),
        'weight-extrabold': String(adminFontWeights.extrabold),
        'weight-black': String(adminFontWeights.black),
        ...Object.fromEntries(styles.map(([name, s]) => [`font-size-${name}`, px(s.fontSize)])),
        ...Object.fromEntries(styles.map(([name, s]) => [`line-height-${name}`, px(s.lineHeight)])),
        'space-half': px(2),
        'tracking-wide': '0.08em',
        'sidebar-w': px(adminLayout.sidebarWidth),
        'topbar-h': px(adminLayout.topbarHeight),
        'panel-xl': px(adminLayout.panel.xl),
        'panel-phone': px(adminLayout.panel.phone),
    };
    // Only real changes: a value equal to the shared stylesheet is left to tokens.css.
    return Object.fromEntries(Object.entries(changed).filter(([name, value]) => shared[name] !== value));
}
function block(selector, declarations, extra = []) {
    const lines = [
        ...extra.map((line) => `  ${line};`),
        ...Object.entries(declarations).map(([name, value]) => `  --${name}: ${value};`),
    ];
    return `${selector} {\n${lines.join('\n')}\n}\n`;
}
const schemeSelector = (scheme) => COLOR_SCHEME_SELECTOR.replace('%s', scheme);
/**
 * The admin stylesheet, loaded after tokens.css with the same selectors so it wins by order: the
 * changed scales on `:root`, then every admin colour role for the light and dark schemes.
 */
export function toAdminCss() {
    return [
        '/* Generated by @lions/ui-tokens (src/admin.ts). Admin web only; load after tokens.css. */\n',
        block(':root', adminScaleVariables()),
        block(`:root,\n${schemeSelector('light')}`, adminSemanticLight),
        block(schemeSelector('dark'), adminSemanticDark),
    ].join('\n');
}
//# sourceMappingURL=admin.js.map